/**
 * PublicInterviewApp - Orchestrator for the public (unauthenticated) interview flow.
 * Renders the appropriate phase component based on hook state.
 */

import { useEffect } from 'react'

import { usePublicInterview } from '@/hooks/usePublicInterview'

import { InterviewLanding } from './InterviewLanding'
import { PublicConversation } from './PublicConversation'
import { InterviewThankYou } from './InterviewThankYou'
import { Loader2, AlertCircle } from 'lucide-react'

// ============================================================================
// COMPONENT
// ============================================================================

interface PublicInterviewAppProps {
  shareToken: string
}

export function PublicInterviewApp({ shareToken }: PublicInterviewAppProps) {
  const {
    phase,
    landing,
    messages,
    mcqOptions,
    isStreaming,
    isStarting,
    error,
    messageCount,
    progress,
    conversationStartedAt,
    fetchLanding,
    startInterview,
    sendMessage,
    completeInterview,
  } = usePublicInterview(shareToken)

  useEffect(() => {
    fetchLanding()
  }, [fetchLanding])

  // Wrap in fixed full-viewport container to escape #root's inset-16px + overflow-hidden
  const content = (() => {
    if (phase === 'loading') {
      return (
        <div className="flex h-full items-center justify-center bg-warm-50">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-business" />
            <p className="text-sm text-warm-500">Loading interview...</p>
          </div>
        </div>
      )
    }

    if (phase === 'error') {
      return (
        <div className="flex h-full items-center justify-center bg-warm-50">
          <div className="flex max-w-md flex-col items-center gap-4 rounded-2xl bg-white p-8 shadow-sm">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <h2 className="font-display text-lg font-semibold text-warm-800">
              Something went wrong
            </h2>
            <p className="text-center text-sm text-warm-500">
              {error ?? 'Unable to load this interview. The link may be invalid or expired.'}
            </p>
          </div>
        </div>
      )
    }

    if (phase === 'landing' && landing) {
      return (
        <InterviewLanding
          landing={landing}
          onStart={startInterview}
          isLoading={isStarting}
        />
      )
    }

    if (phase === 'conversation') {
      return (
        <PublicConversation
          messages={messages}
          mcqOptions={mcqOptions}
          isStreaming={isStreaming}
          progress={progress}
          startedAt={conversationStartedAt}
          estimatedDuration={landing?.estimatedDuration || 30}
          onSendMessage={sendMessage}
          onComplete={completeInterview}
        />
      )
    }

    if (phase === 'completed') {
      return <InterviewThankYou messageCount={messageCount} />
    }

    return null
  })()

  return (
    <div className="fixed inset-0 z-50">
      {content}
    </div>
  )
}
